/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/

(function () {

  // ---------------------------------------------------------------------------
  // Infinite Scroll
  // When new products load, the grid expands downward like an opening drawer —
  // pushing content below it down smoothly — then items fade in as they reveal.
  // Re-initialises automatically after filter/sort AJAX updates the grid.
  // ---------------------------------------------------------------------------

  const EXPAND_DURATION  = 800;   // ms for the grid height expansion
  const FADE_START_DELAY = 200;   // ms into expansion before items start fading in
  const FADE_STAGGER     = 60;    // ms between each item fading in
  const FADE_DURATION    = 500;   // ms for each item fade
  const TRIGGER_MARGIN   = '600px'; // how far before sentinel to trigger fetch

  const css = `
    .infinite-scroll-spinner {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 32px 0;
      width: 100%;
    }
    .infinite-scroll-spinner__ring {
      width: 28px;
      height: 28px;
      border: 2px solid #e0e0e0;
      border-top-color: #000;
      border-radius: 50%;
      animation: is-spin 0.7s linear infinite;
    }
    @keyframes is-spin {
      to { transform: rotate(360deg); }
    }

    /* Remove the staggered fadeIn animation on collection grid swatches so
       they appear the instant JS finishes rendering them. */
    .radio__fieldset--swatches.radio__fieldset--pgi.is-visible .swatch__button {
      animation: none;
    }

    /* Reserve the swatch row height from the start so the card doesn't
       jump when JS renders swatches into the (initially empty) form.
       Mirrors the actual swatch link height (2.5rem * FONT-ADJUST-BODY)
       plus swatch button padding (5px top/bottom) and grid-swatch
       margin compensation (margin: -5px cancels 5px of each padding). */
    .radio__fieldset--pgi {
      min-height: calc(2.5rem * var(--FONT-ADJUST-BODY) + 10px);
    }
  `;

  const styleEl = document.createElement('style');
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  function createSpinner() {
    const wrap = document.createElement('div');
    wrap.className = 'infinite-scroll-spinner';
    wrap.innerHTML = '<div class="infinite-scroll-spinner__ring"></div>';
    return wrap;
  }

  class InfiniteScroll {
    constructor() {
      this.productsGrid  = document.querySelector('[data-products-grid]');
      if (!this.productsGrid) return;

      this.loading       = false;
      this.observer      = null;
      this.sentinel      = null;
      this.spinner       = null;
      this.reinitTimeout = null;

      this.init();
      this.observeGridChanges();
    }

    init() {
      this.collectionLoop = this.productsGrid.querySelector('#CollectionLoop');
      this.paginationArea = this.productsGrid.querySelector('.pagination_page-indicator-wrapper');
      this.paginationNav  = this.productsGrid.querySelector('.pagination-custom');
      this.nextLink       = this.productsGrid.querySelector('a.pagination-custom__next:not(.is-hidden)');

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.productsGrid.querySelector('.infinite-scroll-sentinel')?.remove();
      this.productsGrid.querySelector('.infinite-scroll-spinner')?.remove();
      this.spinner = null;

      if (this.paginationArea) this.paginationArea.style.display = 'none';
      if (this.paginationNav)  this.paginationNav.style.display  = 'none';

      if (!this.nextLink || !this.collectionLoop) return;

      this.sentinel = document.createElement('div');
      this.sentinel.className  = 'infinite-scroll-sentinel';
      this.sentinel.style.cssText = 'height: 1px; width: 100%;';
      this.productsGrid.appendChild(this.sentinel);

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loading) this.loadNextPage();
        });
      }, { rootMargin: TRIGGER_MARGIN });

      this.observer.observe(this.sentinel);
    }

    showSpinner() {
      if (this.spinner) return;
      this.spinner = createSpinner();
      this.productsGrid.insertBefore(this.spinner, this.sentinel);
    }

    hideSpinner() {
      if (this.spinner) { this.spinner.remove(); this.spinner = null; }
    }

    // Pre-populate window.__productJsonCache for incoming items so that
    // grid-swatch custom elements find their JSON already in-flight when
    // they connect to the DOM — eliminating the swatch pop-in delay.
    prefetchSwatchData(items) {
      const cache = window.__productJsonCache;
      if (!cache) return;

      const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
      const seen = new Set();

      items.forEach((item) => {
        item.querySelectorAll('[data-swatch-handle]').forEach((el) => {
          const handle = el.getAttribute('data-swatch-handle');
          if (!handle || cache.has(handle) || seen.has(handle)) return;
          seen.add(handle);
          const p = fetch(`${root}products/${handle}.js`, { credentials: 'same-origin' })
            .then((r) => {
              if (!r.ok) throw new Error(`Product JSON failed (${r.status}) for ${handle}`);
              return r.json();
            })
            .catch((err) => { cache.delete(handle); throw err; });
          cache.set(handle, p);
        });
      });
    }

    async loadNextPage() {
      if (!this.nextLink || this.loading) return;

      this.loading = true;
      this.showSpinner();

      const url = this.nextLink.href;

      try {
        const response = await fetch(url);
        const html     = await response.text();
        const doc      = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = doc.querySelector('#CollectionLoop');
        if (newGrid && this.collectionLoop) {
          const items = [...newGrid.children];
          // Warm the product JSON cache before items connect to the DOM
          this.prefetchSwatchData(items);
          this.hideSpinner();
          await this.expandAndReveal(items);
        }

        const newNextLink = doc.querySelector('a.pagination-custom__next:not(.is-hidden)');
        this.nextLink = newNextLink || null;

        if (!this.nextLink) {
          if (this.observer) this.observer.disconnect();
          if (this.sentinel) this.sentinel.remove();
        }
      } catch (err) {
        this.hideSpinner();
        console.error('[InfiniteScroll] Failed to load page:', err);
      } finally {
        this.loading = false;
      }
    }

    expandAndReveal(items) {
      return new Promise((resolve) => {
        const grid = this.collectionLoop;

        // Snapshot height before new items exist in the DOM
        const startHeight = grid.offsetHeight;

        // Insert items invisibly so the browser can calculate the new layout
        items.forEach((item) => {
          item.style.opacity = '0';
          grid.appendChild(item);
        });

        // Measure the height the grid now wants to be
        const endHeight = grid.offsetHeight;

        // Lock the grid at its current (shorter) height and clip overflow
        // so items below the fold are hidden until we expand
        grid.style.height     = startHeight + 'px';
        grid.style.overflow   = 'hidden';
        grid.style.transition = `height ${EXPAND_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;

        // Two rAF calls ensure the browser has committed the initial state
        // before we change the height (otherwise the transition won't fire)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            grid.style.height = endHeight + 'px';
          });
        });

        // Fade items in during the expansion so they appear to be revealed
        // by the opening drawer rather than popping in afterwards
        items.forEach((item, i) => {
          setTimeout(() => {
            item.style.transition = `opacity ${FADE_DURATION}ms ease`;
            item.style.opacity    = '1';
          }, FADE_START_DELAY + i * FADE_STAGGER);
        });

        // After the expansion finishes, restore normal grid behaviour
        setTimeout(() => {
          grid.style.height     = '';
          grid.style.overflow   = '';
          grid.style.transition = '';
          items.forEach((item) => { item.style.transition = ''; item.style.opacity = ''; });
          resolve();
        }, EXPAND_DURATION + 50);
      });
    }

    observeGridChanges() {
      const gridObserver = new MutationObserver((mutations) => {
        if (this.loading) return;

        const hasExternalChange = mutations.some((mutation) =>
          [...mutation.addedNodes, ...mutation.removedNodes].some(
            (node) => !(node.classList && node.classList.contains('infinite-scroll-sentinel'))
          )
        );

        if (hasExternalChange) {
          clearTimeout(this.reinitTimeout);
          this.reinitTimeout = setTimeout(() => this.init(), 150);
        }
      });

      gridObserver.observe(this.productsGrid, { childList: true });
    }
  }

  // Pre-fetch product JSON for all swatch handles already on the page so
  // grid-swatch custom elements find their data in cache immediately.
  function prefetchPageSwatches() {
    const cache = window.__productJsonCache;
    if (!cache) return;
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    document.querySelectorAll('[data-swatch-handle]').forEach((el) => {
      const handle = el.getAttribute('data-swatch-handle');
      if (!handle || cache.has(handle)) return;
      const p = fetch(`${root}products/${handle}.js`, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error(`Product JSON failed (${r.status}) for ${handle}`);
          return r.json();
        })
        .catch((err) => { cache.delete(handle); throw err; });
      cache.set(handle, p);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      prefetchPageSwatches();
      new InfiniteScroll();
    });
  } else {
    prefetchPageSwatches();
    new InfiniteScroll();
  }

})();
